import type { NearbyCity, SeoData } from '@/shared/api/generated/api';

interface IBuildAroundHeadParams {
  seo: SeoData;
  cities: NearbyCity[];
  /** Картинка по умолчанию, если у SEO-данных её нет (например, фото первого города) */
  fallbackImage?: string;
}

const toAbsoluteUrl = (value: string | undefined, base: string): string => {
  if (!value) return '';

  try {
    return new URL(value, base || undefined).toString();
  } catch {
    return '';
  }
};

const getOrigin = (canonicalUrl: string): string => {
  try {
    return new URL(canonicalUrl).origin;
  } catch {
    return '';
  }
};

/**
 * Мета-теги (Open Graph, Twitter), canonical и JSON-LD (ItemList городов) для страницы «Куда поехать».
 * Возвращает объект для `useHead`.
 */
export const buildAroundHead = ({ seo, cities, fallbackImage }: IBuildAroundHeadParams) => {
  const { title, description, canonical_url: canonicalUrl, noindex } = seo;
  const origin = getOrigin(canonicalUrl);
  const base = origin || canonicalUrl;
  const image = toAbsoluteUrl(seo.image || fallbackImage, base);

  const meta = [
    { name: 'description', content: description },
    { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' },
    { property: 'og:type', content: 'website' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonicalUrl },
    { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    ...(image
      ? [
          { property: 'og:image', content: image },
          { name: 'twitter:image', content: image },
        ]
      : []),
  ];

  // BreadcrumbList не добавляем: его уже отдаёт сам компонент хлебных крошек (UiBreadcrumbs).
  const cityItems = cities
    .map((city) => ({ name: city.name, url: toAbsoluteUrl(city.url, base) }))
    .filter((city) => city.name && city.url)
    .map((city, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: city.name,
      url: city.url,
    }));

  const jsonLd =
    cityItems.length > 0
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: seo.h1 || title,
            itemListElement: cityItems,
          },
        ]
      : [];

  return {
    title,
    meta,
    link: [{ rel: 'canonical', href: canonicalUrl }],
    script: jsonLd.map((data) => ({ type: 'application/ld+json', innerHTML: JSON.stringify(data) })),
  };
};
